import { useEffect, useState } from "react";
import { useAuthStore } from "@/lib/auth";
import { useSessionLogsStore } from "@/lib/stores/sessionLogs";
import { getCurrentHospitalId } from "@/lib/hospitalContext";
import { supabase } from "@/lib/supabase";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Search, Info } from "lucide-react";
import { format } from "date-fns";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

export function UserSessionLogs() {
  const { user } = useAuthStore();
  const [searchQuery, setSearchQuery] = useState("");
  const { logs, loading, error, fetchLogs, clearLogs } = useSessionLogsStore();

  useEffect(() => {
    clearLogs();
    fetchLogs();
  }, [user?.id, fetchLogs, clearLogs]);

  // Realtime: refresh logs on new inserts for this hospital only
  useEffect(() => {
    if (user?.role !== 'admin') return;

    let channel: ReturnType<typeof supabase.channel> | null = null;

    const setup = async () => {
      const hospitalId = await getCurrentHospitalId();
      if (!hospitalId) return;

      channel = supabase
        .channel(`realtime:user_session_logs:${hospitalId}`)
        .on(
          'postgres_changes',
          {
            event: 'insert',
            schema: 'public',
            table: 'user_session_logs',
            filter: `hospital_id=eq.${hospitalId}`,
          },
          () => {
            fetchLogs();
          }
        )
        .subscribe();
    };

    setup();

    return () => {
      if (channel) {
        try {
          supabase.removeChannel(channel);
        } catch (_) {}
      }
    };
  }, [user?.id, user?.role, fetchLogs]);

  // Only show this component to admin users
  if (user?.role !== 'admin') {
    return null;
  }

  // Only show doctor/staff/user (exclude admin)
  const roleFiltered = logs.filter(log => log.userRole === 'doctor' || log.userRole === 'staff' || log.userRole === 'user');
  const filteredLogs = roleFiltered.filter(log =>
    log.userName.toLowerCase().includes(searchQuery.toLowerCase()) ||
    log.userRole.toLowerCase().includes(searchQuery.toLowerCase()) ||
    log.action.toLowerCase().includes(searchQuery.toLowerCase()) ||
    log.details.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getActionBadgeVariant = (action: string) => {
    switch (action) {
      case 'login':
        return 'default';
      case 'logout':
        return 'destructive';
      case 'view':
        return 'secondary';
      case 'edit':
        return 'secondary'; // changed from 'warning' to 'secondary'
      case 'delete':
        return 'destructive';
      default:
        return 'outline';
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">User Session Logs</h2>
          <p className="text-xs text-muted-foreground">Showing logs from the last 30 days.</p>
        </div>
        <div className="relative w-64">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search logs..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-8"
          />
        </div>
      </div>

      {loading && <div>Loading...</div>}
      {error && <div className="text-red-500">Error: {error}</div>}

      <div className="rounded-md border overflow-auto max-h-[60vh]">
        <Table>
          <TableHeader className="sticky top-0 z-10 bg-background">
            <TableRow className="h-10">
              <TableHead className="py-2 px-3 text-xs font-medium">User</TableHead>
              <TableHead className="py-2 px-3 text-xs font-medium">Email</TableHead>
              <TableHead className="py-2 px-3 text-xs font-medium">Role</TableHead>
              <TableHead className="py-2 px-3 text-xs font-medium">Action</TableHead>
              <TableHead className="py-2 px-3 text-xs font-medium">Details</TableHead>
              <TableHead className="py-2 px-3 text-xs font-medium">Timestamp</TableHead>
              <TableHead className="py-2 px-3 text-xs font-medium">IP Address</TableHead>
              <TableHead className="py-2 px-3 text-xs font-medium w-[16rem]">Device</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredLogs.length === 0 && !loading && !error && (
              <TableRow>
                <TableCell colSpan={8} className="py-6 text-center text-sm text-muted-foreground">
                  No doctor or staff session logs found. Perform a login/logout as staff/doctor to see entries.
                </TableCell>
              </TableRow>
            )}
            {filteredLogs.map((log) => (
              <TableRow key={log.id} className="h-12">
                <TableCell className="py-2 px-3 font-medium text-sm">{log.userName}</TableCell>
                <TableCell className="py-2 px-3 text-sm">{log.userEmail || '-'}</TableCell>
                <TableCell className="py-2 px-3 capitalize text-sm">{log.userRole}</TableCell>
                <TableCell className="py-2 px-3">
                  <Badge variant={getActionBadgeVariant(log.action)} className="text-xs px-2 py-0.5">
                    {log.action}
                  </Badge>
                </TableCell>
                <TableCell className="py-2 px-3 text-sm">
                  {log.details ? (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            className="inline-flex h-6 w-6 items-center justify-center rounded hover:bg-muted/50"
                            aria-label="View details"
                          >
                            <Info className="h-3.5 w-3.5 text-muted-foreground" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-[60vw] break-words whitespace-pre-wrap text-xs">
                          {log.details}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  ) : (
                    <span className="text-muted-foreground text-xs">-</span>
                  )}
                </TableCell>
                <TableCell className="py-2 px-3 text-xs">
                  {format(new Date(log.timestamp), 'MMM d, yyyy HH:mm:ss')}
                </TableCell>
                <TableCell className="py-2 px-3 text-sm">{log.ipAddress}</TableCell>
                <TableCell className="py-2 px-3 text-xs text-muted-foreground max-w-[16rem] whitespace-nowrap overflow-hidden text-ellipsis">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="inline-block align-middle cursor-help text-xs">
                          {log.deviceInfo}
                        </span>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-[70vw] break-words whitespace-pre-wrap text-xs">
                        {log.deviceInfo}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
} 